import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TimelineItem } from '../state/types'
import {
  clockTime, formatDuration, isApiCall, isTraceMarker, summarizeActivity,
} from '../util/activity'
import { Timeline } from './Timeline'

interface ActivityLogProps {
  /** One run of agent work — status/tool/error items only, never an operator turn. */
  items: TimelineItem[]
  /** The filenames the operator attached, computed once for the whole thread by SessionBody. */
  attached: Set<string>
  /** True while the run is live — the log opens so the operator watches the work happen. */
  expanded: boolean
  /** True while the session is still live — the headline must not claim past-tense completion. */
  running: boolean
  /** The turn's trace marker for the headline, resolved by SessionBody; admin-only. */
  trace?: string
}

/** Icon for a step, chosen from what the wire actually says. The trace marker (the one client-made row)
 *  first; then the step the service described; an older service's frame by its `METHOD /path` shape. */
function stepIcon(item: TimelineItem): string {
  if (item.kind !== 'tool') return 'fa-info-circle'
  if (isTraceMarker(item.summary)) return 'fa-fingerprint'
  if (item.activity) {
    if (item.activity.type === 'read') return 'fa-file-alt'
    return item.activity.methodClass === 'read' ? 'fa-search' : 'fa-pen'
  }
  if (!isApiCall(item.summary)) return 'fa-file-alt'
  return item.summary.startsWith('GET ') ? 'fa-search' : 'fa-pen'
}

/** How a work segment turned out, read off its own items rather than the session's phase — plus
 *  `running`, an override from the session's live/terminal status rather than the items. */
type ActivityOutcome = 'ok' | 'failed' | 'stopped' | 'running'

const OUTCOME_ICON: Record<ActivityOutcome, string> = {
  ok: 'fa-clipboard-check',
  failed: 'fa-exclamation-triangle',
  stopped: 'fa-ban',
  // Not fa-spinner: it reads as an animation without actually rotating here, which is exactly
  // what the operator misread as a broken spin. RunningStep already owns the one genuinely
  // animated affordance; this just needs to say "in progress".
  running: 'fa-ellipsis-h',
}

/** A real failure outranks an operator-cancelled run: `failed` wins if both are present. Both
 *  outrank `running` — a segment that already recorded an error stays that way even if the
 *  session as a whole hasn't reached a terminal phase yet. */
function deriveOutcome(items: TimelineItem[], running: boolean): ActivityOutcome {
  const errors = items.filter((item): item is Extract<TimelineItem, { kind: 'error' }> => item.kind === 'error')
  if (errors.some((e) => !e.cancelled)) return 'failed'
  if (errors.some((e) => e.cancelled)) return 'stopped'
  if (running) return 'running'
  return 'ok'
}

export function ActivityLog({
  items, attached, expanded, running, trace,
}: Readonly<ActivityLogProps>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(expanded)
  // Once the operator has toggled this log by hand, its open state is theirs: the automatic
  // sync below must never overwrite it again, no matter how many more times `expanded` changes
  // (a session going terminal, then live again on a follow-up turn, etc). A ref, not state,
  // because flipping it must not itself trigger a render or re-run the effect.
  const operatorSet = useRef(false)

  // Follow the run: auto-open while live, collapse once it finishes — until the operator takes
  // the toggle into their own hands, above.
  useEffect(() => {
    if (!operatorSet.current) setOpen(expanded)
  }, [expanded])

  if (items.length === 0) return null
  const { documents, apiCalls, durationMs } = summarizeActivity(items, attached)
  const outcome = deriveOutcome(items, running)
  const hasCounts = documents > 0 || apiCalls > 0
  const rootClassName = outcome === 'ok'
    ? 'copilot-activity'
    : `copilot-activity copilot-activity--${outcome}`

  return (
    <div className={rootClassName}>
      <button
        type="button"
        className="copilot-activity__toggle"
        data-testid="copilot-activity-toggle"
        aria-expanded={open}
        onClick={() => {
          operatorSet.current = true
          setOpen((o) => !o)
        }}
      >
        <i className={`fa ${OUTCOME_ICON[outcome]}`} aria-hidden="true" />
        <span className="copilot-activity__headline" data-testid="copilot-activity-summary">
          {outcome === 'ok' && (
            <>
              {`${t('copilot.activity.worked')} `}
              <span data-testid="copilot-activity-duration">{formatDuration(durationMs)}</span>
            </>
          )}
          {outcome === 'failed' && t('copilot.activity.failed')}
          {outcome === 'stopped' && t('copilot.activity.stopped')}
          {outcome === 'running' && t('copilot.activity.inProgress')}
          {hasCounts && (
            <span className="copilot-activity__counts">
              {' · '}
              <span data-testid="copilot-activity-documents">{documents}</span>
              {` ${t('copilot.activity.documents')} · `}
              <span data-testid="copilot-activity-apicalls">{apiCalls}</span>
              {` ${t('copilot.activity.apiCalls')}`}
            </span>
          )}
          {trace && (
            <code className="copilot-activity__trace" data-testid="copilot-activity-trace" title={trace}>{` · ${trace}`}</code>
          )}
        </span>
        <i className={`fa ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="copilot-activity__steps">
          <Timeline items={items} renderStepIcon={stepIcon} renderStepTime={clockTime} />
        </div>
      )}
    </div>
  )
}
